import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { generateQuotePDF, downloadPDF } from '../../../utils/pdfGenerator';
import { supabase } from '../../../lib/supabase';

const QuotesTable = ({ quotes, loading, onViewDetails, onSendQuote, onApproveQuote, onConvertToTicket, onEditQuote, onDeleteQuote, onOpenApproval, onDuplicateQuote }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return 'bg-muted text-muted-foreground border-border';
      case 'sent':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'approved':
        return 'bg-success/10 text-success border-success/20';
      case 'rejected':
        return 'bg-error/10 text-error border-error/20';
      case 'converted':
        return 'bg-secondary/10 text-secondary border-secondary/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0)?.toFixed(2)}`;
  };

  const handleDownloadPDF = async (quote, e) => {
    e?.stopPropagation();
    const { data: orgData } = await supabase?.from('organizations')?.select('*')?.limit(1)?.single();
    const html = generateQuotePDF(quote, orgData || {});
    downloadPDF(html, `quote-${quote?.quote_number}.pdf`);
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-8 flex items-center justify-center">
        <Icon name="Loader2" size={24} className="animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading quotes...</span>
      </div>
    );
  }

  if (quotes?.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-8 text-center">
        <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Quotes Found</h3>
        <p className="text-muted-foreground">Create your first quote to get started</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground">
                Quote ID
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground">
                Customer
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground hidden lg:table-cell">
                Salesperson
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground">
                Total Amount
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground hidden xl:table-cell">
                Expected Service
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground">
                Status
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {quotes?.map((quote) => (
              <tr key={quote?.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-smooth">
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <span className="text-sm font-medium text-foreground">{quote?.quote_number}</span>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{quote?.customer?.name}</p>
                    <p className="text-xs text-muted-foreground">{quote?.customer?.email}</p>
                  </div>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4 hidden lg:table-cell">
                  <span className="text-sm text-foreground">{quote?.salesperson?.name || 'N/A'}</span>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <span className="text-sm font-semibold text-foreground">{formatCurrency(quote?.total_amount)}</span>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4 hidden xl:table-cell">
                  <span className="text-sm text-foreground">{formatDate(quote?.expected_service_date)}</span>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(quote?.status?.name)}`}>
                    {quote?.status?.name}
                  </span>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onViewDetails(quote)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Download"
                      onClick={(e) => handleDownloadPDF(quote, e)}
                      title="Download PDF"
                    />
                    {onDuplicateQuote && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Copy"
                        onClick={() => onDuplicateQuote(quote)}
                        title="Duplicate Quote"
                      />
                    )}
                    {onOpenApproval && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="GitBranch"
                        onClick={() => onOpenApproval(quote)}
                        title="Approval Workflow"
                      />
                    )}
                    {quote?.status?.name === 'Draft' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Edit"
                          onClick={() => onEditQuote(quote)}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Send"
                          onClick={() => onSendQuote(quote?.id)}
                        />
                      </>
                    )}
                    {quote?.status?.name === 'Sent' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Edit"
                          onClick={() => onEditQuote(quote)}
                        />
                        <Button
                          variant="success"
                          size="sm"
                          iconName="CheckCircle"
                          onClick={() => onApproveQuote(quote?.id)}
                        />
                      </>
                    )}
                    {quote?.status?.name === 'Approved' && (
                      <Button
                        variant="primary"
                        size="sm"
                        iconName="ArrowRight"
                        onClick={() => onConvertToTicket(quote)}
                      />
                    )}
                    {(quote?.status?.name === 'Draft' || quote?.status?.name === 'Sent') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => onDeleteQuote(quote?.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuotesTable;